import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMembers,
  createMember,
  updateMember,
  updateMemberContact,
  deleteMember,
  getStudyAssociations,
  uploadMemberLogo,
  deleteMemberLogo,
  type Member,
  type StudyAssociation
} from '../lib/admin';
import { getUser } from '../lib/auth';
import { Building2, Edit, Trash2, Plus, Users, UserCog, Brain, CheckCircle, XCircle, Image, GraduationCap, FileText } from 'lucide-react';
import LogoUpload from './LogoUpload';
import { useToast, useConfirm } from './ui';

export default function MemberManagement() {
  const currentUser = getUser();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [members, setMembers] = useState<Member[]>([]);
  const [studyAssociations, setStudyAssociations] = useState<StudyAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [editingContact, setEditingContact] = useState<Member | null>(null);
  const [contactForm, setContactForm] = useState({ address: '', contact_phone: '', contact_email: '', technical_email: '' });
  const [saving, setSaving] = useState(false);
  const [filterStudyAssociation, setFilterStudyAssociation] = useState('');

  const isMemberAdmin = currentUser?.role === 'member' && (currentUser as any)?.is_member_admin === true;
  const canEditFull = currentUser?.role === 'systemadmin' || currentUser?.role === 'admin';

  // Form state
  const [formData, setFormData] = useState({
    organization_name: '',
    organization_number: '',
    contact_email: '',
    technical_email: '',
    contact_phone: '',
    address: '',
    study_association_id: '',
    status: 'active'
  });

  useEffect(() => {
    loadData();
  }, [filterStudyAssociation]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const associationsData = await getStudyAssociations();
      setStudyAssociations(associationsData);

      // Load members, optionally filtered by study association
      const membersData = await getMembers(filterStudyAssociation || undefined);
      setMembers(membersData);

    } catch (err: any) {
      setError(err.message || 'Kunne ikke laste medlemmer');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.organization_name || !formData.study_association_id) {
      toast.showWarning('Organisasjonsnavn og studieforbund er påkrevd');
      return;
    }

    try {
      setSaving(true);
      await createMember(formData);
      toast.showSuccess('Medlemsorganisasjon opprettet!');
      setShowCreateForm(false);
      resetForm();
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved opprettelse: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingMember) return;

    try {
      setSaving(true);
      await updateMember(editingMember.id, formData);

      // Clear filter if study association was changed
      if (formData.study_association_id !== editingMember.study_association_id) {
        setFilterStudyAssociation('');
      }

      toast.showSuccess('Medlemsorganisasjon oppdatert!');
      setEditingMember(null);
      resetForm();
      document.body.style.overflow = 'unset';
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved oppdatering: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (member: Member) => {
    if (currentUser.role !== 'systemadmin') {
      toast.showWarning('Kun systemadmin kan deaktivere medlemsorganisasjoner');
      return;
    }

    const confirmed = await confirm({
      title: 'Deaktiver medlemsorganisasjon',
      message: `Er du sikker på at du vil deaktivere ${member.organization_name}?\n\nAlle tilknyttede brukere og data vil beholdes, men organisasjonen vil ikke være aktiv.`,
      confirmLabel: 'Deaktiver',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      await deleteMember(member.id);
      toast.showSuccess(`${member.organization_name} er deaktivert`);
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil ved deaktivering: ${err.message}`);
    }
  };

  const startEdit = (member: Member) => {
    setEditingMember(member);
    setFormData({
      organization_name: member.organization_name,
      organization_number: member.organization_number || '',
      contact_email: member.contact_email || '',
      technical_email: member.technical_email || '',
      contact_phone: member.contact_phone || '',
      address: member.address || '',
      study_association_id: member.study_association_id,
      status: member.status
    });
    setShowCreateForm(false);

    // Lock body scroll when modal opens
    document.body.style.overflow = 'hidden';
  };

  const resetForm = () => {
    setFormData({
      organization_name: '',
      organization_number: '',
      contact_email: '',
      technical_email: '',
      contact_phone: '',
      address: '',
      study_association_id: currentUser.role === 'admin' ? currentUser.study_association_id : '',
      status: 'active'
    });
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
    setShowCreateForm(false);
    resetForm();
    document.body.style.overflow = 'unset';
  };

  const startContactEdit = (member: Member) => {
    setEditingContact(member);
    setContactForm({
      address: member.address || '',
      contact_phone: member.contact_phone || '',
      contact_email: member.contact_email || '',
      technical_email: member.technical_email || ''
    });
    document.body.style.overflow = 'hidden';
  };

  const handleCancelContactEdit = () => {
    setEditingContact(null);
    setContactForm({ address: '', contact_phone: '', contact_email: '', technical_email: '' });
    document.body.style.overflow = 'unset';
  };

  const handleSaveContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingContact) return;
    try {
      setSaving(true);
      await updateMemberContact(editingContact.id, {
        address: contactForm.address.trim() || null,
        contact_phone: contactForm.contact_phone.trim() || null,
        contact_email: contactForm.contact_email.trim() || null,
        technical_email: contactForm.technical_email.trim() || null
      });
      toast.showSuccess('Kontaktinformasjon oppdatert');
      handleCancelContactEdit();
      await loadData();
    } catch (err: any) {
      toast.showError(`Feil: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const navigateToSensors = (memberId: string) => {
    navigate(`/members/${memberId}/sensors`);
  };

  const navigateToUsers = (memberId: string) => {
    navigate(`/members?tab=users&member_id=${memberId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B5651D] mx-auto mb-4"></div>
          <p className="text-gray-600">Laster medlemmer...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Building2 className="w-8 h-8 text-[#B5651D]" />
            <h1 className="text-3xl font-bold text-gray-900">
              {currentUser.role === 'member' ? 'Min organisasjon' : 'Medlemsorganisasjoner'}
            </h1>
          </div>
          {(currentUser.role === 'systemadmin' || currentUser.role === 'admin') && (
            <button
              onClick={() => {
                setShowCreateForm(true);
                setEditingMember(null);
                resetForm();
              }}
              className="flex items-center space-x-2 bg-[#B5651D] text-white px-4 py-2 rounded-lg hover:bg-[#8B4513] transition"
            >
              <Plus className="w-5 h-5" />
              <span>Ny Organisasjon</span>
            </button>
          )}
        </div>
        <p className="text-gray-600">
          {currentUser.role === 'member'
            ? 'Informasjon om din organisasjon registrert hos studieforbundet'
            : 'Administrer medlemsorganisasjoner tilknyttet studieforbund'}
        </p>
      </div>

      <QualityContractSection />


      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Filter Section */}
      {currentUser.role === 'systemadmin' && (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Filtrer på studieforbund:</label>
            <select
              value={filterStudyAssociation}
              onChange={(e) => setFilterStudyAssociation(e.target.value)}
              className="flex-1 max-w-md rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
            >
              <option value="">Alle studieforbund</option>
              {studyAssociations.map(sa => (
                <option key={sa.id} value={sa.id}>{sa.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Create Form (inline) */}
      {showCreateForm && !editingMember && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Opprett Ny Organisasjon
          </h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organisasjonsnavn *
                </label>
                <input
                  type="text"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organisasjonsnummer
                </label>
                <input
                  type="text"
                  value={formData.organization_number}
                  onChange={(e) => setFormData({ ...formData, organization_number: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Studieforbund *
                </label>
                <select
                  value={formData.study_association_id}
                  onChange={(e) => setFormData({ ...formData, study_association_id: e.target.value })}
                  required
                  disabled={currentUser.role === 'admin'}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                >
                  <option value="">Velg studieforbund</option>
                  {studyAssociations.map(sa => (
                    <option key={sa.id} value={sa.id}>{sa.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kontakt-e-post
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="post@organisasjonen.no"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
                <p className="mt-1 text-xs text-gray-500">Vises på attester.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teknisk e-post
                </label>
                <input
                  type="email"
                  value={formData.technical_email}
                  onChange={(e) => setFormData({ ...formData, technical_email: e.target.value })}
                  placeholder="drift@organisasjonen.no"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
                <p className="mt-1 text-xs text-gray-500">Mottar systemvarsler. Tom = bruk kontakt-e-post.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kontakt Telefon
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={2}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
              />
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition disabled:opacity-50"
              >
                {saving ? 'Lagrer...' : 'Opprett'}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Rediger Organisasjon
            </h2>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organisasjonsnavn *
                  </label>
                  <input
                    type="text"
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    required
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organisasjonsnummer
                  </label>
                  <input
                    type="text"
                    value={formData.organization_number}
                    onChange={(e) => setFormData({ ...formData, organization_number: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Studieforbund *
                  </label>
                  <select
                    value={formData.study_association_id}
                    onChange={(e) => setFormData({ ...formData, study_association_id: e.target.value })}
                    required
                    disabled={currentUser.role === 'admin'}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  >
                    <option value="">Velg studieforbund</option>
                    {studyAssociations.map(sa => (
                      <option key={sa.id} value={sa.id}>{sa.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kontakt-e-post
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="post@organisasjonen.no"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  />
                  <p className="mt-1 text-xs text-gray-500">Vises på kompetanseattester (kursarrangør).</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teknisk e-post
                  </label>
                  <input
                    type="email"
                    value={formData.technical_email}
                    onChange={(e) => setFormData({ ...formData, technical_email: e.target.value })}
                    placeholder="drift@organisasjonen.no"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  />
                  <p className="mt-1 text-xs text-gray-500">Mottar systemvarsler (LMS, sync). Tom = bruk kontakt-e-post.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kontakt Telefon
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                  />
                </div>

                {currentUser.role === 'systemadmin' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                    >
                      <option value="active">Aktiv</option>
                      <option value="inactive">Inaktiv</option>
                      <option value="suspended">Suspendert</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={2}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#B5651D] focus:ring-[#B5651D]"
                />
              </div>

              {/* Logo Upload */}
              <LogoUpload
                currentLogoPath={editingMember.logo_path}
                onUpload={async (file) => {
                  const result = await uploadMemberLogo(editingMember.id, file);
                  if (result.success) {
                    setEditingMember({
                      ...editingMember,
                      logo_path: result.logo_path || null
                    });
                    loadData();
                  }
                  return result;
                }}
                onDelete={async () => {
                  await deleteMemberLogo(editingMember.id);
                  setEditingMember({
                    ...editingMember,
                    logo_path: null
                  });
                  loadData();
                }}
                disabled={saving}
              />

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#B5651D] text-white py-2 rounded-lg hover:bg-[#8B4513] transition disabled:opacity-50"
                >
                  {saving ? 'Lagrer...' : 'Oppdater'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organisasjon
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Studieforbund
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kontakt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statistikk
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {members.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    Ingen medlemsorganisasjoner funnet
                  </td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr
                    key={member.id}
                    className={`hover:bg-gray-50 ${isMemberAdmin && !canEditFull ? 'cursor-pointer' : ''}`}
                    onClick={isMemberAdmin && !canEditFull ? () => startContactEdit(member) : undefined}
                    title={isMemberAdmin && !canEditFull ? 'Klikk for å redigere kontaktinformasjon' : undefined}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {member.logo_path ? (
                          <img
                            src={`${import.meta.env.VITE_API_URL || ''}${member.logo_path}`}
                            alt={member.organization_name}
                            className="h-8 w-8 object-contain flex-shrink-0"
                          />
                        ) : (
                          <Building2 className="h-8 w-8 text-gray-400 flex-shrink-0" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            {member.organization_name}
                            {member.logo_path && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                <Image size={10} className="mr-0.5" />
                                Logo
                              </span>
                            )}
                          </div>
                          {member.organization_number && (
                            <div className="text-sm text-gray-500">
                              Org.nr: {member.organization_number}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {member.study_association_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{member.contact_email || 'Ingen e-post'}</div>
                      <div className="text-sm text-gray-500">{member.contact_phone || 'Ingen telefon'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center text-sm text-gray-900">
                          <Users className="w-4 h-4 mr-1" />
                          <span>{member.user_count || 0} brukere</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Users className="w-4 h-4 mr-1" />
                          <span>{member.sensor_count || 0} sensorer</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Brain className="w-4 h-4 mr-1" />
                          <span>{member.ai_requests_count} AI-forespørsler</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        member.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : member.status === 'suspended'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {member.status === 'active' ? (
                          <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Aktiv
                          </>
                        ) : member.status === 'suspended' ? (
                          'Suspendert'
                        ) : (
                          <>
                            <XCircle className="w-3 h-3 mr-1" />
                            Inaktiv
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center space-x-2">
                        {canEditFull && (
                          <button
                            onClick={() => navigateToUsers(member.id)}
                            className="text-blue-600 hover:text-blue-800 transition"
                            title="Vis brukere"
                          >
                            <UserCog className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => navigateToSensors(member.id)}
                          className="text-[#B5651D] hover:text-[#8B4513] transition"
                          title="Administrer sensorer"
                        >
                          <Users className="w-5 h-5" />
                        </button>
                        {canEditFull && (
                          <>
                            <button
                              onClick={() => startEdit(member)}
                              className="text-green-600 hover:text-green-800 transition"
                              title="Rediger organisasjon"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            {currentUser.role === 'systemadmin' && member.status === 'active' && (
                              <button
                                onClick={() => handleDelete(member)}
                                className="text-red-600 hover:text-red-800 transition"
                                title="Deaktiver"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </>
                        )}
                        {!canEditFull && isMemberAdmin && (
                          <button
                            onClick={() => startContactEdit(member)}
                            className="text-green-600 hover:text-green-800 transition"
                            title="Rediger kontaktinformasjon"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats — kun for admin/systemadmin (member ser bare egen org) */}
      {members.length > 0 && currentUser.role !== 'member' && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totalt Medlemmer</p>
                <p className="text-2xl font-bold text-gray-900">{members.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-[#B5651D]" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totalt Brukere</p>
                <p className="text-2xl font-bold text-gray-900">
                  {members.reduce((sum, m) => sum + Number(m.user_count || 0), 0)}
                </p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totalt Sensorer</p>
                <p className="text-2xl font-bold text-gray-900">
                  {members.reduce((sum, m) => sum + Number(m.sensor_count || 0), 0)}
                </p>
              </div>
              <GraduationCap className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totalt AI-forespørsler</p>
                <p className="text-2xl font-bold text-gray-900">
                  {members.reduce((sum, m) => sum + Number(m.ai_requests_count || 0), 0)}
                </p>
              </div>
              <Brain className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {/* Kontaktinfo-modal — for medlem-admin */}
      {editingContact && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleCancelContactEdit}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              Rediger kontaktinformasjon
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {editingContact.organization_name}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-sm text-blue-900 space-y-2">
              <p>To e-postadresser med ulik funksjon:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Kontakt-e-post</strong> vises på kompetanseattester (kursarrangør)
                  og er den offentlige adressen for spørsmål fra kandidater.
                </li>
                <li>
                  <strong>Teknisk e-post</strong> mottar systemvarsler: vedtak ved
                  LMS-godkjenninger, sync-feil og sikkerhetsvarsler. Hvis tom, brukes
                  kontakt-e-posten som fallback.
                </li>
              </ul>
              <p className="text-xs text-blue-800">
                Kontakt-e-post oppdateres umiddelbart på alle nye attester som lastes ned eller
                åpnes etterpå. Originalt signerte PDF-filer er ikke endret.
                Organisasjonsnavn, organisasjonsnummer og studieforbund-tilknytning
                kan kun endres av studieforbund-administrator eller systemadmin.
              </p>
            </div>

            <form onSubmit={handleSaveContact} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <textarea
                  value={contactForm.address}
                  onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })}
                  rows={2}
                  placeholder="Gateadresse, postnummer og sted"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-kf-primary focus:ring-kf-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefonnummer (kontakt)
                </label>
                <input
                  type="tel"
                  value={contactForm.contact_phone}
                  onChange={(e) => setContactForm({ ...contactForm, contact_phone: e.target.value })}
                  placeholder="f.eks. +47 22 33 44 55"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-kf-primary focus:ring-kf-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kontakt-e-post
                </label>
                <input
                  type="email"
                  value={contactForm.contact_email}
                  onChange={(e) => setContactForm({ ...contactForm, contact_email: e.target.value })}
                  placeholder="f.eks. post@organisasjonen.no"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-kf-primary focus:ring-kf-primary"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Vises på kompetanseattester. Påvirker ikke pålogging.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teknisk e-post (systemvarsler)
                </label>
                <input
                  type="email"
                  value={contactForm.technical_email}
                  onChange={(e) => setContactForm({ ...contactForm, technical_email: e.target.value })}
                  placeholder="f.eks. drift@organisasjonen.no"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-kf-primary focus:ring-kf-primary"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Mottar LMS-godkjenninger, sync-feil og sikkerhetsvarsler.
                  Vises ikke på attester. Hvis tom, brukes kontakt-e-post.
                </p>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-kf-primary text-white py-2 rounded-lg hover:bg-kf-dark transition disabled:opacity-50"
                >
                  {saving ? 'Lagrer…' : 'Lagre'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelContactEdit}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Viser status for kvalitetssikrings-kontrakten + nedlastningsknapp.
 * Synlig for alle innloggede roller — innholdet tilpasses status:
 *   - Ikke signert: lenke til onboarding-flyten
 *   - Signert: dato, signatør, versjon + "Last ned PDF"-knapp
 *   - Ny versjon tilgjengelig: oppfordring til å signere på nytt
 */
function QualityContractSection() {
  const [status, setStatus] = useState<any>(null);
  const [contractId, setContractId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/onboarding/status', {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` }
        });
        const data = await res.json();
        if (data.success) {
          setStatus(data);
          // Hvis signert finnes, hent kontrakt-id (via samme status-data eller separat call hvis nødvendig)
          if (data.organization_signed) {
            // status returnerer ikke kontrakt-id direkte; vi henter via en separat liste-rute
            try {
              const ctrRes = await fetch('/api/onboarding/contract-id', {
                headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` }
              });
              if (ctrRes.ok) {
                const ctrData = await ctrRes.json();
                if (ctrData.success && ctrData.contract_id) setContractId(ctrData.contract_id);
              }
            } catch {}
          }
        }
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  if (!status || status.reason === 'no_member_organization') return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 mb-6">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-md bg-kf-primary text-white flex items-center justify-center flex-shrink-0">
          <FileText className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-gray-900">Kvalitetssikrings-kontrakt</h2>
          {status.organization_signed ? (
            <>
              <p className="text-sm text-gray-700 mt-1">
                Signert versjon <strong>{status.organization_signed_version}</strong> den{' '}
                {new Date(status.organization_signed_at).toLocaleDateString('nb-NO', { dateStyle: 'long' })}
                {status.organization_signed_by ? ` av ${status.organization_signed_by}` : ''}.
              </p>
              {!status.org_signed_this_version && (
                <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  En ny versjon ({status.current_template_version}) er tilgjengelig.
                  Organisasjonen må signere den nye versjonen for å fortsette.
                </p>
              )}
              {contractId && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await fetch(`/api/onboarding/contract-pdf/${contractId}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` }
                      });
                      if (!res.ok) { alert('Kunne ikke hente PDF'); return; }
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      window.open(url, '_blank');
                      setTimeout(() => URL.revokeObjectURL(url), 60_000);
                    } catch (e: any) {
                      alert(`Feil: ${e.message || e}`);
                    }
                  }}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-kf-primary text-kf-primary rounded-md hover:bg-kf-primary hover:text-white transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Last ned signert kontrakt (PDF)
                </button>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-gray-700 mt-1">
                Organisasjonen har ikke signert kvalitetssikrings-kontrakten ennå.
              </p>
              <a
                href="/onboarding/quality-assurance"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-dark"
              >
                Gå til signering
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
